<template>
	<header
		id="navigation"
		:class="{
			'bg-[#052647] lg:bg-transparent fixed top-0 left-0 w-full text-smarta-blue-500 z-50': true,
			'navigation-not-intersecting': !intersecting,
			'navigation-initial': initialised
		}"
	>
		<div class="container h-full">
			<div class="flex justify-between w-full h-full">
				<div class="flex items-center px-4 px-0 w-full justify-between">
					<a href="/" class="block" aria-label="Return to homepage">
		                <img
		                	src="/images/logos/transmit-nav-logo.svg"
		                	:class="{
		                		'image-transition': true,
		                		'h-[55px]': !intersecting,
		                		'h-[123px]': intersecting
		                	}"
		                >
		            </a>
                    <button
                    	type="button"
                    	aria-label="Toggle navigation"
                    	class="text-white lg:hidden"
                    	@click.prevent="expanded = !expanded"
                    >
                        <fa-icon
                            :icon="['fas', 'bars']"
                            :class="{
								'transition-all duration-500': true,
								'rotate-90': expanded,
								'rotate-0': !expanded
							}"
                            size="2x"
                            fixed-width
                        />
                    </button>
				</div>
				<nav
					v-if="expanded"
					id="nav_submenu"
					class="flex absolute lg:static lg:flex flex-col lg:flex-row items-center w-screen lg:h-full lg:w-auto bg-white lg:bg-transparent overflow-y-auto lg:overflow-y-visible lg:pt-3 lg:pb-[80px] lg:!pt-0 lg:pb-0 z-40"
					role="navigation"
				>
					<ul class="flex flex-col lg:flex-row items-center justify-center w-full border-t lg:border-0 lg:box-unset border-[#607d8b]">
                        <template v-for="item in items">
                            <navigation-dropdown
                            	v-if="item.children && item.children.length > 0"
                                :item="item"
                                :items="item.children"
                            />
                            <navigation-item
                            	v-else
                            	:item="item"
                            	class="bg-[#102f4e] lg:bg-transparent border-b lg:border-0 border-[#607d8b] text-white px-5 lg:px-0 pb-1"
                            />
                        </template>
                        <navigation-search class="hidden lg:block" />
                        <navigation-item
                        	:item="applyItem"
                        	class="bg-[#102f4e] lg:bg-transparent border-b lg:border-0 border-[#607d8b] text-white text-base px-5 lg:px-2"
                        />
					</ul>
				</nav>
			</div>
		</div>
		<a
			v-if="ctaEnabled && ctaLink && ctaLabel"
			id="cta_banner__mobile"
			prop="callToAction"
			target="_blank"
			:href="ctaLink"
			:class="{
				'absolute top-full left-0 w-full block text-white transition-all': true,
				'xl:translate-y-0': !intersectingCta,
				'xl:-translate-y-full': intersectingCta,
				'bg-transparent': intersecting,
				'bg-[#e4097e]': !intersecting
			}"
		>
			<div class="container">
				<div
					:class="{
						'flex flex-col xl:flex-row items-center justify-center rounded': true,
						'bg-black/25 py-4': intersecting,
						'bg-transparent py-2': !intersecting
					}"
				>
					<span class="text-sm xl:text-base font-bold text-center">{{ ctaLabel }}</span>
				</div>
			</div>
		</a>
	</header>
</template>
<script>
import { mixin as clickaway } from 'vue-clickaway';

export default {
	name: 'Navigation',

	mixins: [
		clickaway
	],

	props: {
		items: {
			type: Array,
			required: true
		},

		ctaEnabled: {
			type: Boolean,
			required: false,
			default: () => { return false }
		},

		ctaLink: {
			type: String,
			required: false,
			default: () => { return null }
		},

		ctaLabel: {
			type: String,
			required: false,
			default: () => { return null }
		}
	},

	data () {
		return {
			expanded: false,
			observer: null,
			observerCta: null,
			observerCtaElem: 'cta_banner__desktop',
			intersecting: true,
			initialised: false,
			dropdown: null,
			applyItem: {
            	primary: true,
                children: false,
                label: 'GET STARTED',
                permalink: '/apply'
           	}
		}
	},

	mounted () {
	  	this.$nextTick(function () {
	    	this.initialised = true
	  	})
	},

	watch: {
		expanded (newVal) {
			document.documentElement.classList[newVal ? 'add' : 'remove']('scroll-lock-mobile')
		}
	},

	created () {
		this.startObserving();
		this.startObservingCta();

        if (window.innerWidth > 1024) {
            this.expanded = true;
        }
	},

	destroyed () {
		this.stopObserving();
		this.stopObservingCta();
	},

	methods: {
		startObserving () {
			const threshold = 10
			this.observer = new IntersectionObserver((entries, observer) => {
			    entries.forEach((entry) => {
			    	this.intersecting = entry.isIntersecting
			    })
		  	}, {
		  		threshold: 0,
		  		rootMargin: `${threshold}px 0px 0px 0px`
		  	})
		  	this.$nextTick(() => {
		  		this.observer.observe(document.getElementById('top'))
		  	})
		},

		stopObserving () {
			this.$nextTick(() => {
				this.observer.unobserve(document.getElementById('top'))
			})
		},

		startObservingCta () {
			const elem = document.getElementById(this.observerCtaElem)
			if (elem) {
				this.observerCta = new IntersectionObserver((entries, observer) => {
				    entries.forEach((entry) => {
				    	this.intersectingCta = entry.isIntersecting
				    })
			  	}, {
			  		threshold: 0,
			  		rootMargin: '-120px 0px 0px 0px'
			  	})
			  	this.$nextTick(() => {
			  		this.observerCta.observe(elem)
			  	})
			} else {
				this.intersectingCta = false
			}
		},

		stopObservingCta () {
			this.$nextTick(() => {
				if (this.observerCta) {
					this.observerCta.unobserve(document.getElementById(this.observerCtaElem))
				}
			})
		},
	}
}
</script>
<style scoped>
	#navigation {
		display: none;
        max-height:120px !important;
        transition: all 0.15s ease-out;
        height:100%;
	}
    #nav_submenu
    {
        top:120px;
        left:0;
        transition: all 0.15s ease-out;
    }
    .navigation-not-intersecting #nav_submenu{
        top:65px;
    }
	#navigation.navigation-initial {
		display: flex;
	}
	#navigation.navigation-not-intersecting {
    	background: #052647 !important;
        max-height:65px !important;
    	filter: drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1));
    	display: flex;
	}
    .image-transition
    {
        transition: all 0.15s ease-out;
    }
</style>
